// =============================================================================
// TOLOnews Data Architecture Types
// Based on TOLOnews_Data_Architecture_Specification.md
// =============================================================================

// =============================================================================
// ENUMS - Match PostgreSQL enum types
// =============================================================================

// Publication status (IPTC standard)
export type PubStatus = 'usable' | 'withheld' | 'canceled';

// Content/item types
export type ItemType = 'text' | 'video' | 'audio' | 'picture' | 'graphic' | 'composite' | 'live_blog';

// Platforms
export type Platform = 'tv' | 'radio' | 'web' | 'social' | 'all';

// Desks
export type Desk = 'tv' | 'radio' | 'web' | 'social' | 'international';

// Breaking news levels
export type BreakingLevel = 'flash' | 'urgent' | 'developing';

// Workflow states (full lifecycle)
export type WorkflowStatus =
  | 'idea' | 'pitched' | 'assigned'                                    // Planning
  | 'draft' | 'ai_review' | 'submitted' | 'editing' | 'in_review' | 'needs_revision' | 'approved' | 'scheduled'  // Production (ai_review is journalist-side AI check before formal submission)
  | 'published' | 'updated'                                            // Published
  | 'killed' | 'withdrawn' | 'archived';                               // Terminal

// Planning item status
export type PlanningStatus = 'planned' | 'confirming' | 'assigned' | 'in_progress' | 'covered' | 'canceled';

// Version types (wire service pattern)
export type VersionType = 'initial' | 'take' | 'update' | 'correction' | 'writethru';

// Signal types (IPTC processing instruction)
export type SignalType = 'update' | 'correction';

// Translation status
export type TranslationStatus = 'not_started' | 'ai_draft' | 'in_review' | 'approved' | 'published';

// Translation method
export type TranslationMethod = 'human' | 'machine' | 'machine_post_edited';

// Adaptation level
export type AdaptationLevel = 'verbatim' | 'light_edit' | 'localized' | 'regional_adaptation' | 'independent';

// Collection types
export type CollectionType = 'rundown' | 'web_feed' | 'social_queue' | 'radio_lineup';

// Ordering modes for collections
export type OrderingMode = 'time_sequenced' | 'priority_based' | 'manual';

// Collection status
export type CollectionStatus = 'planning' | 'locked' | 'on_air' | 'completed' | 'archived';

// Broadcast item types
export type BroadcastItemType = 'VO' | 'SOT' | 'PKG' | 'LIVE' | 'BREAK' | 'TEASE' | 'BUMP';

// Approval stages
export type ApprovalStage = 'copy_edit' | 'section_review' | 'senior_approval' | 'legal';

// Approval decisions
export type ApprovalDecision = 'approved' | 'rejected' | 'needs_revision' | 'pending';

// Component types (lower thirds, etc.)
export type ComponentType = 'lower_third' | 'quote_card' | 'locator' | 'headline_graphic';

// MOS statuses
export type MosStatus = 'sent' | 'acknowledged' | 'aired';
export type MosItemStatus = 'pending' | 'ready' | 'cued' | 'on_air' | 'done';

// User roles
export type UserRole = 'journalist' | 'editor' | 'senior_editor' | 'admin' | 'superuser' | 'suspended' | 'producer' | 'supervisor';


// =============================================================================
// LANGUAGE - BCP47 codes
// =============================================================================

export type LanguageCode = 'fa-AF' | 'ps-AF' | 'eng';

// =============================================================================
// ORGANIZATION DEFAULTS - Change these to configure system-wide defaults
// =============================================================================

/** Default language for new content (change to 'ps-AF' for Pashto or 'eng' for English) */
export const DEFAULT_LANGUAGE: LanguageCode = 'fa-AF';

/** Default platform for new assignments */
export const DEFAULT_PLATFORM: Platform = 'tv';

/** All supported languages in display order */
export const SUPPORTED_LANGUAGES: LanguageCode[] = ['fa-AF', 'ps-AF', 'eng'];

/** All supported platforms in display order */
export const SUPPORTED_PLATFORMS: Platform[] = ['tv', 'web', 'social'];

/** Default item type for new content (video for TV) */
export const DEFAULT_ITEM_TYPE: ItemType = 'video';

// =============================================================================
// TV FORMAT - Short vs Full Report
// =============================================================================

export type TvFormat = 'short' | 'full';

/** Default TV format for new TV stories */
export const DEFAULT_TV_FORMAT: TvFormat = 'short';

export const TV_FORMAT_LABELS: Record<TvFormat, string> = {
  'short': 'TV Short (20-60 sec)',
  'full': 'TV Full Report (1:30-2:30)',
};

export const TV_FORMAT_SHORT_LABELS: Record<TvFormat, string> = {
  'short': 'Short',
  'full': 'Full Report',
};

// =============================================================================
// PLATFORM METADATA - TV-specific fields stored as JSONB
// =============================================================================

/**
 * Platform-specific metadata stored in the `platform_metadata` JSONB column.
 * Required on SimpleStory (NOT optional), defaults to `{}` for web stories.
 * DB column stores snake_case keys; mapDbToStory converts to camelCase.
 */
export interface PlatformMetadata {
  slug?: string;
  anchorIntro?: string;
  script?: string;
  tvFormat?: TvFormat;
  durationTarget?: number;
  durationEstimate?: number;
}

// =============================================================================
// TV DURATION - Estimation and Targets
// =============================================================================

/**
 * Default words per second (fallback when pacing config not loaded)
 * @deprecated Use TvPacingConfig from database instead
 */
export const WORDS_PER_SECOND = 2.5;

/** Target duration in seconds for each TV format */
export const TV_FORMAT_DURATION_TARGETS: Record<TvFormat, number> = {
  'short': 45,   // 20-60 sec, target midpoint
  'full': 120,   // 1:30-2:30, target midpoint
};

/**
 * Language-specific pacing configuration from database
 */
export interface TvPacingConfig {
  anchorWps: number;  // Words per second for anchor intro
  scriptWps: number;  // Words per second for script/VO
}

/**
 * Word count targets for a TV format/language combination
 */
export interface TvWordTargets {
  anchorWords: number;   // Target word count for anchor intro
  scriptWords: number;   // Target word count for script
  durationTarget: number; // Target duration in seconds
}

/**
 * Detailed duration breakdown with separate anchor/script calculations
 */
export interface DurationBreakdown {
  anchorWords: number;
  scriptWords: number;
  totalWords: number;
  anchorDuration: number;   // seconds
  scriptDuration: number;   // seconds
  totalDuration: number;    // seconds
  anchorTarget: number;     // target duration for anchor
  scriptTarget: number;     // target duration for script
}

/**
 * Default pacing config (fallback when database not available)
 */
export const DEFAULT_PACING: Record<LanguageCode, TvPacingConfig> = {
  'fa-AF': { anchorWps: 2.13, scriptWps: 2.46 },
  'ps-AF': { anchorWps: 2.00, scriptWps: 2.33 },
  'eng':   { anchorWps: 2.50, scriptWps: 2.50 },
};

/**
 * Count words in text (handles RTL languages properly)
 */
export const countWords = (text: string): number => {
  const trimmed = (text || '').trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).filter(Boolean).length;
};

/**
 * Estimate duration in seconds based on word count (legacy, uses fixed rate)
 * @deprecated Use estimateDurationDetailed with language-specific pacing
 */
export const estimateDuration = (anchorIntro: string, script: string): number => {
  const text = ((anchorIntro || '') + ' ' + (script || '')).trim();
  if (!text) return 0;
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.round(words / WORDS_PER_SECOND);
};

/**
 * Calculate detailed duration breakdown with language-specific pacing
 * @param anchorIntro - Anchor introduction text
 * @param script - Main script/voice-over content
 * @param pacing - Language-specific pacing config
 * @param wordTargets - Optional word count targets
 * @returns Detailed breakdown of words and durations
 */
export const estimateDurationDetailed = (
  anchorIntro: string,
  script: string,
  pacing: TvPacingConfig,
  wordTargets?: TvWordTargets
): DurationBreakdown => {
  const anchorWords = countWords(anchorIntro);
  const scriptWords = countWords(script);
  const totalWords = anchorWords + scriptWords;

  const anchorDuration = Math.round(anchorWords / pacing.anchorWps);
  const scriptDuration = Math.round(scriptWords / pacing.scriptWps);
  const totalDuration = anchorDuration + scriptDuration;

  // Calculate target durations for anchor and script
  // Default: 15s for anchor, rest for script
  const anchorTarget = wordTargets
    ? Math.round(wordTargets.anchorWords / pacing.anchorWps)
    : 15;
  const scriptTarget = wordTargets
    ? Math.round(wordTargets.scriptWords / pacing.scriptWps)
    : 45 - 15;

  return {
    anchorWords,
    scriptWords,
    totalWords,
    anchorDuration,
    scriptDuration,
    totalDuration,
    anchorTarget,
    scriptTarget,
  };
};

/**
 * Format duration as MM:SS string
 * @param seconds - Duration in seconds
 * @returns Formatted string like "1:45" or "0:30"
 */
export const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Get duration status color class based on estimate vs target
 * @param estimate - Estimated duration in seconds
 * @param target - Target duration in seconds
 * @returns Tailwind color class (green, yellow, or red)
 */
export const getDurationStatusColor = (estimate: number, target: number): string => {
  if (target === 0) return 'text-zinc-500';
  const ratio = estimate / target;
  // Within 10% = green, within 25% = yellow, outside = red
  if (ratio >= 0.9 && ratio <= 1.1) return 'text-green-600 dark:text-green-400';
  if (ratio >= 0.75 && ratio <= 1.25) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
};

/**
 * Get status icon for duration comparison
 */
export const getDurationStatusIcon = (estimate: number, target: number): string => {
  if (target === 0) return '';
  const ratio = estimate / target;
  if (ratio >= 0.9 && ratio <= 1.1) return '✓';
  if (ratio >= 0.75 && ratio <= 1.25) return '⚠';
  return '✗';
};

// =============================================================================

export const LANGUAGE_LABELS: Record<LanguageCode, string> = {
  'fa-AF': 'دری',      // Dari
  'ps-AF': 'پښتو',     // Pashto
  'eng': 'English',
};

export const LANGUAGE_NAMES: Record<LanguageCode, string> = {
  'fa-AF': 'Dari',
  'ps-AF': 'Pashto',
  'eng': 'English',
};

// Legacy enum for backwards compatibility
export enum Language {
  ENGLISH = 'english',
  DARI = 'dari',
  PASHTO = 'pashto',
}

// Map legacy Language enum to BCP47 codes
export const LEGACY_TO_BCP47: Record<Language, LanguageCode> = {
  [Language.ENGLISH]: 'eng',
  [Language.DARI]: 'fa-AF',
  [Language.PASHTO]: 'ps-AF',
};

export const BCP47_TO_LEGACY: Record<LanguageCode, Language> = {
  'eng': Language.ENGLISH,
  'fa-AF': Language.DARI,
  'ps-AF': Language.PASHTO,
};


// =============================================================================
// USERS
// =============================================================================

export interface User {
  id: string;
  email: string;
  full_name: string;
  role: UserRole;
  desk?: Desk;
  avatar_url?: string;
  is_active: boolean;
  created_at: string;
  last_login?: string | null;
}


// =============================================================================
// STORIES (The Hub)
// =============================================================================

export interface Story {
  id: string;
  guid: string;                        // urn:newsml:tolonews.com:YYYYMMDD:SLUG
  slug: string;                        // Human-readable: AFG-KABUL-ELECTION-2025
  canonical_language: LanguageCode;
  urgency: number;                     // 1-9 IPTC scale, 1=highest
  pub_status: PubStatus;
  is_breaking: boolean;
  breaking_level?: BreakingLevel;
  subjects: IPTCSubject[];             // IPTC Media Topics with QCodes
  created_at: string;
  created_by?: string;
  updated_at: string;
  archived_at?: string;
}

export interface IPTCSubject {
  qcode: string;                       // e.g., "medtop:20000538"
  name: string;                        // English name
  name_fa?: string;                    // Dari name
  name_ps?: string;                    // Pashto name
}

// Helper to generate story GUID
export function generateStoryGuid(slug: string): string {
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  return `urn:newsml:tolonews.com:${date}:${slug}`;
}


// =============================================================================
// PLANNING ITEMS (The Intent)
// =============================================================================

export interface PlanningItem {
  id: string;
  story_id: string;
  title?: string;                      // Assignment title: "TV Package (Dari)"
  assigned_desk: Desk;
  platform: Platform;                  // Target platform: tv, radio, web, social
  language: LanguageCode;              // Target language: fa-AF, ps-AF, eng
  item_type: ItemType;
  assigned_to?: string;
  due_date?: string;
  priority: number;                    // 1-100
  status: PlanningStatus;
  notes?: string;
  created_at: string;
  created_by?: string;
  promoted_to_content_at?: string;
}

export interface PlanningItemWithUser extends PlanningItem {
  assigned_user?: User;                // Populated when fetching with joins
  content_item?: ContentItem;          // The content item created from this planning item
  story?: Story;                       // Populated when fetching with joins
}


// =============================================================================
// CONTENT ITEMS (The Assets/Spokes)
// =============================================================================

export interface ContentItem {
  id: string;
  guid: string;
  story_id: string;
  planning_id?: string;
  language: LanguageCode;
  is_canonical: boolean;
  derived_from_content_id?: string;
  item_type: ItemType;
  platform: Platform;
  current_version_id?: string;
  published_version_id?: string;
  workflow_status: WorkflowStatus;

  // Breaking news fast-track
  fast_track_authorized_by?: string;
  fast_track_authorized_at?: string;
  skipped_approvals?: string[];
  post_publication_review_required: boolean;
  post_publication_reviewed_by?: string;
  post_publication_reviewed_at?: string;

  // MOS integration
  mos_item_id?: string;
  mos_obj_type?: string;
  mos_status?: MosStatus;
  mos_last_update?: string;

  // Assignment
  assigned_to?: string;

  // Timestamps
  created_at: string;
  created_by?: string;
  published_at?: string;
  archived_at?: string;
  updated_at: string;
}

// Helper to generate content item GUID
export function generateContentGuid(storySlug: string, language: LanguageCode, platform: Platform): string {
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  return `urn:newsml:tolonews.com:${date}:${storySlug}-${language}-${platform}`;
}


// =============================================================================
// CONTENT VERSIONS (Immutable Audit Log)
// =============================================================================

export interface ContentVersion {
  id: string;
  content_item_id: string;
  version_number: number;
  version_type: VersionType;
  signal?: SignalType;
  headline?: string;
  summary?: string;
  body_text?: string;
  body_ninjs: NinjsPayload;            // Full ninjs-compliant JSON
  duration_seconds?: number;
  editorial_note?: string;             // Required for corrections
  created_by: string;
  created_at: string;
  is_auto_save: boolean;
}

// ninjs (News in JSON) payload structure
export interface NinjsPayload {
  uri?: string;
  type?: ItemType;
  versioncreated?: string;
  firstcreated?: string;
  language?: LanguageCode;
  urgency?: number;
  pubstatus?: PubStatus;
  headline?: string;
  slugline?: string;
  byline?: string;
  located?: string;
  body_text?: string;
  body_html?: string;
  description_text?: string;
  subjects?: IPTCSubject[];
  associations?: Record<string, NinjsAssociation>;
  renditions?: Record<string, NinjsRendition>;

  // TV/Radio specific fields
  duration?: number;
  anchor_intro?: string;
  outcue?: string;
  segments?: ContentSegment[];

  // Custom TOLOnews fields
  graphics_required?: string[];
  video_slugs?: string[];
  seo_title?: string;
  seo_description?: string;
  keywords?: string[];
  featured_image_url?: string;
  featured_image_caption?: string;
  category?: string;
  tags?: string[];
}

export interface NinjsAssociation {
  uri?: string;
  type?: ItemType;
  headline?: string;
}

export interface NinjsRendition {
  href?: string;
  contenttype?: string;
  width?: number;
  height?: number;
}

// Content segment (for TV/Radio scripts)
export interface ContentSegment {
  id: string;
  type: 'anchor' | 'vo' | 'sot' | 'pkg_intro' | 'standup' | 'graphics' | 'actuality' | 'wrap' | 'voicer';
  text: string;
  duration: number;
  video_notes?: string;
  sot_speaker?: string;
  sot_title?: string;
  outcue?: string;
}


// =============================================================================
// TRANSLATIONS
// =============================================================================

export interface Translation {
  id: string;
  source_content_id: string;
  target_content_id: string;
  source_language: LanguageCode;
  target_language: LanguageCode;
  translation_status: TranslationStatus;
  translation_method?: TranslationMethod;
  mt_engine?: string;
  confidence_score?: number;           // 0.00-1.00
  adaptation_level: AdaptationLevel;
  ai_disclosure_required: boolean;
  translator_id?: string;
  reviewer_id?: string;
  source_last_modified?: string;
  is_stale: boolean;
  created_at: string;
  completed_at?: string;
}


// =============================================================================
// COLLECTIONS (Rundowns + Digital Queues)
// =============================================================================

export interface Collection {
  id: string;
  slug: string;                        // e.g., "6PM-NEWS-2025-01-16"
  type: CollectionType;
  ordering_mode: OrderingMode;
  platform: Platform;
  channel?: string;                    // e.g., "TOLO-TV"
  target_datetime?: string;            // Air time for rundowns
  scheduled_duration?: string;         // ISO 8601 duration
  status: CollectionStatus;

  // MOS integration
  mos_ro_id?: string;
  mos_ro_slug?: string;
  ncs_id: string;

  // Locking
  locked_by?: string;
  locked_at?: string;

  // Audit
  created_at: string;
  created_by?: string;
  updated_at: string;
}


// =============================================================================
// COLLECTION ITEMS
// =============================================================================

export interface CollectionItem {
  id: string;
  collection_id: string;
  content_item_id: string;
  story_id?: string;                   // Denormalized

  // Ordering
  sequence_order?: number;

  // Time-sequenced (broadcast)
  planned_start?: string;
  planned_duration?: string;           // ISO 8601 duration
  back_time?: string;
  segment_code?: string;               // e.g., "A1", "B3"
  item_type_broadcast?: BroadcastItemType;

  // Priority-based (digital)
  priority?: number;                   // 1-100
  is_pinned: boolean;
  pinned_until?: string;
  pinned_by?: string;
  is_featured: boolean;

  // MOS integration
  mos_item_status?: MosItemStatus;
  mos_item_channel?: string;
  last_mos_update?: string;

  // Status
  status: 'scheduled' | 'active' | 'complete' | 'killed';

  // Audit
  created_at: string;
  added_by?: string;
}


// =============================================================================
// COMPONENTS (Lower Thirds, Quote Cards)
// =============================================================================

export interface Component {
  id: string;
  story_id: string;
  component_type: ComponentType;
  canonical_language: LanguageCode;
  translations: ComponentTranslations;
  timecode_in?: string;                // ISO 8601 duration
  timecode_out?: string;
  is_active: boolean;
  graphics_template_id?: string;
  graphics_data?: Record<string, unknown>;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface ComponentTranslations {
  [language: string]: {
    line1: string;
    line2?: string;
  };
}


// =============================================================================
// COORDINATED RELEASES
// =============================================================================

export interface CoordinatedRelease {
  id: string;
  story_id: string;
  go_live_time: string;
  status: 'scheduled' | 'pending_verification' | 'released' | 'canceled';
  notes?: string;
  created_by?: string;
  created_at: string;
}

export interface ReleaseTarget {
  id: string;
  release_id: string;
  collection_id?: string;
  content_item_id?: string;
  platform: Platform;
  status: 'scheduled' | 'pending_verification' | 'live' | 'failed';
  verified_by?: string;
  verified_at?: string;
  actual_live_time?: string;
}


// =============================================================================
// WORKFLOW
// =============================================================================

export interface WorkflowDefinition {
  id: string;
  name: string;
  description?: string;
  content_type?: ItemType;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
}

export interface WorkflowState {
  id: string;
  workflow_id: string;
  state_name: string;
  state_type: 'planning' | 'production' | 'published' | 'terminal';
  display_order: number;
  is_initial: boolean;
  is_final: boolean;
  color: string;
}

export interface WorkflowTransition {
  id: string;
  workflow_id: string;
  from_state: string;
  to_state: string;
  transition_name: string;
  required_roles?: string[];
  auto_transition: boolean;
  creates_notification: boolean;
}

export interface WorkflowHistory {
  id: string;
  content_item_id: string;
  from_status?: string;
  to_status: string;
  transition_name?: string;
  triggered_by?: string;
  triggered_at: string;
  notes?: string;
  skipped_reason?: string;
}


// =============================================================================
// APPROVALS
// =============================================================================

export interface Approval {
  id: string;
  content_item_id: string;
  content_version_id?: string;
  approval_stage: ApprovalStage;
  approver_id?: string;
  decision: ApprovalDecision;
  submitted_at: string;
  decided_at?: string;
  comments?: string;
  revision_requested_fields?: string[];
}


// =============================================================================
// EDITORIAL COMMENTS
// =============================================================================

export interface EditorialComment {
  id: string;
  content_item_id: string;
  content_version_id?: string;
  author_id: string;
  comment_text: string;
  field_reference?: string;
  position_start?: number;
  position_end?: number;
  is_resolved: boolean;
  resolved_by?: string;
  resolved_at?: string;
  created_at: string;
}


// =============================================================================
// UPDATE NOTIFICATIONS
// =============================================================================

export interface UpdateNotification {
  id: string;
  source_content_id: string;
  source_version?: number;
  target_type: 'translation' | 'output';
  target_id: string;
  notification_created: string;
  acknowledged_at?: string;
  acknowledged_by?: string;
  action_taken?: 'updated' | 'no_action_needed' | 'deferred';
}


// =============================================================================
// AUDIT LOG
// =============================================================================

export interface AuditLog {
  id: string;
  user_id?: string;
  action: string;
  entity_type?: string;
  entity_id?: string;
  old_values?: Record<string, unknown>;
  new_values?: Record<string, unknown>;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}


// =============================================================================
// TERMINOLOGY
// =============================================================================

export interface Terminology {
  id: string;
  term_en?: string;
  term_fa?: string;
  term_ps?: string;
  context?: string;
  notes?: string;
  created_at: string;
}


// =============================================================================
// API RESPONSE TYPES
// =============================================================================

export interface StoryWithContent extends Story {
  planning_items?: PlanningItemWithUser[];
  content_items?: ContentItemWithVersion[];
  components?: Component[];
}

export interface ContentItemWithVersion extends ContentItem {
  current_version?: ContentVersion;
  published_version?: ContentVersion;
  translation?: Translation;
  assigned_user?: User;  // Populated when fetching with joins
}

export interface CollectionWithItems extends Collection {
  items?: CollectionItemWithContent[];
}

export interface CollectionItemWithContent extends CollectionItem {
  content_item?: ContentItemWithVersion;
  story?: Story;
}


// =============================================================================
// AI SERVICE TYPES
// =============================================================================

export interface JournalistAgentRequest {
  headline: string;
  deck: string;
  body: string;
  language: LanguageCode;
  feedback?: string;
}

export interface JournalistAgentResponse {
  headline: string;
  deck: string;
  body: string;
}

export interface CriticAgentRequest {
  headline: string;
  deck: string;
  body: string;
  language: LanguageCode;
  isTranslation?: boolean;
}

export interface CriticAgentResponse {
  issues: EditorIssue[];
}

export interface EditorIssue {
  id?: string;
  category: 'ai_fixable' | 'human_required' | 'custom';
  text: string;
  suggestion?: string;
  applied?: boolean;
  // Issue lifecycle tracking (pending = default for backward compat)
  status?: 'pending' | 'applied' | 'dismissed';
  dismissedAt?: string;
  appliedAt?: string;
  // Phase 2a: Style Critic fields
  // Phase 2b: Journalism Critic fields (attribution, sourcing, logic, completeness, balance)
  // Phase 2c: Fact Checker fields (fact_check)
  // Phase 2d: Deep Check fields (fact_check with sources)
  type?: 'grammar' | 'demonym' | 'format' | 'terminology' | 'style'
       | 'attribution' | 'sourcing' | 'logic' | 'completeness' | 'balance'
       | 'fact_check';
  // Location can be web article fields OR TV script fields
  location?: 'headline' | 'deck' | 'body' | 'slug' | 'anchor_intro' | 'script';
  original?: string;
  // Source indicator - which critic found this issue
  source?: 'style_critic' | 'journalism_critic' | 'fact_checker' | 'deep_check';
  // Phase 2c/2d: Confidence level for fact-check issues
  confidence?: 'verified' | 'unverified' | 'uncertain';
  // Phase 2d: Source URLs from Tavily search (deep_check only)
  sources?: string[];
  // Phase 2e: Priority level assigned by Curator
  priority?: 'critical' | 'high' | 'medium' | 'low';
}

export interface EditorFeedbackCard {
  id: string;
  location: 'headline' | 'deck' | 'body' | 'anchor_intro' | 'script';
  text: string;
  suggestion?: string;
  quotedText?: string; // Captured text selection from the editor field
  severity?: 'must_fix' | 'suggestion' | 'note';
  createdAt: string;
  createdBy: string;
  createdByName?: string;
  resolved?: boolean;
  resolvedAt?: string;
}

export interface TranslatorAgentRequest {
  headline: string;
  deck: string;
  body: string;
  sourceLanguage: LanguageCode;
  targetLanguage: LanguageCode;
}

export interface TranslatorAgentResponse {
  headline: string;
  deck: string;
  body: string;
}

export interface SocialAgentRequest {
  headline: string;
  deck: string;
  body: string;
  language: LanguageCode;
}

export interface SocialAgentResponse {
  posts: SocialPost[];
}

export interface SocialPost {
  platform: 'twitter' | 'facebook' | 'telegram' | 'instagram' | 'tiktok';
  text: string;
  character_count: number;
  hashtags?: string[];
}


// =============================================================================
// PLATFORM-LANGUAGE CONFIGURATION
// =============================================================================

/**
 * Configuration for valid platform-language combinations.
 * Used to enforce that TV broadcasts are only in Dari/Pashto (not English).
 */
export interface PlatformLanguageConfig {
  platform: Platform;
  language: LanguageCode;
  allowed: boolean;
  isCanonicalSource: boolean;  // True if this combo can be a canonical source
}

// =============================================================================
// TV JOURNALIST TYPES
// =============================================================================

/**
 * Request for TV journalist agent - polishes TV scripts
 */
export interface TvJournalistAgentRequest {
  slug: string;
  anchor_intro: string;
  script: string;
  language: LanguageCode;
  duration_target: number;
  feedback?: string;
}

/**
 * Response from TV journalist agent - outputs TV script structure
 * When called with story context, also includes fact storage status
 */
export interface TvJournalistAgentResponse {
  slug: string;
  anchor_intro: string;
  script: string;
  // Fact storage status (populated when story context is provided)
  facts_stored?: boolean;
  facts_count?: number;
  facts_storage_error?: string;
}

// =============================================================================
// REVIEW MODE
// =============================================================================

/**
 * Review mode determines which critics run.
 * - 'full': All 4 critics (style, journalism, fact-check, deep-check) + curator
 * - 'light': Only style critic (for derivatives - web articles, translations)
 */
export type ReviewMode = 'full' | 'light';

// =============================================================================
// STORY GRID TYPES (Phase 3 - Derivative Generation)
// =============================================================================

/**
 * Simple story status for Phase 3 workflow.
 * - draft: Initial state, being written/edited
 * - reviewed: AI-reviewed, ready for approval
 * - approved: Locked, ready for derivative generation
 * - archived: No longer active
 *
 * Note: Legacy values (ai_review, ready, published) are still in the DB enum
 * but no longer used by the application.
 */
export type StoryStatus = 'draft' | 'reviewed' | 'pending_review' | 'approved' | 'archived' | 'ai_review' | 'ready' | 'published';

/** Display labels for story statuses */
export const STORY_STATUS_LABELS: Record<StoryStatus, string> = {
  draft: 'Draft',
  reviewed: 'AI Reviewed',
  pending_review: 'Pending Review',
  approved: 'Approved',
  archived: 'Archived',
  ai_review: 'AI Review',
  ready: 'Ready',
  published: 'Published',
};

/** Tailwind color classes for story status badges */
export const STORY_STATUS_COLORS: Record<StoryStatus, string> = {
  draft: 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400',
  reviewed: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
  pending_review: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  approved: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  archived: 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400',
  ai_review: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
  ready: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  published: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
};

/** Roles with editor-level permissions */
export const EDITOR_ROLES: readonly string[] = ['editor', 'senior_editor', 'admin', 'superuser', 'supervisor', 'producer'] as const;

/**
 * A single cell in the story grid, representing one platform-language combination.
 */
export interface StoryGridCell {
  platform: Platform;
  language: LanguageCode;
  story: import('./services/simpleStore').SimpleStory | null;
  isCanonical: boolean;
  isDisabled: boolean;  // From platform_language_config (e.g., English TV is disabled)
}

/**
 * Complete grid data for displaying all versions of a story group.
 */
export interface StoryGridData {
  groupId: string;
  canonicalStory: import('./services/simpleStore').SimpleStory;
  grid: Record<Platform, Record<LanguageCode, import('./services/simpleStore').SimpleStory | null>>;
  platformLanguageConfig: PlatformLanguageConfig[];
  title: string;  // From slug or headline of canonical
}

/**
 * Response from web rewriter agent.
 * Converts TV script to web article format.
 */
export interface WebRewriterResponse {
  headline: string;
  deck: string;
  body: string;
}

/**
 * Response from translator agent.
 * Returns content in the same format as input.
 */
export interface TranslatorResponse {
  // Web article format
  headline?: string;
  deck?: string;
  body?: string;
  // TV script format
  slug?: string;
  anchor_intro?: string;
  script?: string;
}

// =============================================================================
// STORY FACTS (Facts Stream - Phase 0)
// =============================================================================

/**
 * Fact type categorizes the kind of factual information.
 */
export type FactType = 'claim' | 'quote' | 'statistic' | 'event' | 'background';

/**
 * Source type categorizes who/what provided the information.
 */
export type SourceType = 'official' | 'witness' | 'document' | 'wire' | 'unattributed';

/**
 * Completeness indicates whether all source information is present.
 */
export type FactCompleteness = 'complete' | 'partial';

/**
 * Confidence level for a fact (updated by fact-checking critics).
 */
export type FactConfidence = 'verified' | 'attributed' | 'unverified' | 'disputed';

/**
 * A fact extracted from a story.
 * Facts are atomic units of verified information that can be reused across
 * different story versions (TV, web, social) and translations.
 */
export interface StoryFact {
  id: string;
  content: string;
  fact_type: FactType;
  language: string;
  source_attribution?: string;
  source_type?: SourceType;
  completeness: FactCompleteness;
  completeness_note?: string;
  confidence: FactConfidence;
  confidence_note?: string;
  extracted_at: string;
  extracted_from_story?: string;
  superseded_by?: string;
  is_current?: boolean;
}

/**
 * Fact confidence update from critics (fact_checker, deep_check).
 * Returned by critics when they verify facts from the story_facts table.
 */
export interface FactUpdate {
  fact_id: string;
  new_confidence: 'verified' | 'unverified' | 'disputed';
  confidence_note?: string;
  sources?: string[];  // URLs from search results, deep_check only
}

/**
 * Input format for passing facts to verification critics.
 * Used when calling fact_checker or deep_check agents.
 */
export interface FactForVerification {
  id: string;
  content: string;
  fact_type: FactType;
  source_attribution?: string;
  confidence: FactConfidence;
}

// =============================================================================
// STORY REVISIONS (ENPS Parity - Revision History)
// =============================================================================

/**
 * An append-only snapshot of a story's full row state.
 * Created automatically by the snapshot_story_revision AFTER UPDATE trigger
 * whenever editorial content (headline, deck, body, anchor_intro, script) changes.
 */
export interface StoryRevision {
  id: string;
  storyId: string;
  snapshot: Record<string, unknown>;
  changedBy?: string;
  createdAt: string;
}


// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

// Get workflow status category
export function getWorkflowCategory(status: WorkflowStatus): 'planning' | 'production' | 'published' | 'terminal' {
  const planning: WorkflowStatus[] = ['idea', 'pitched', 'assigned'];
  const production: WorkflowStatus[] = ['draft', 'submitted', 'editing', 'in_review', 'needs_revision', 'approved', 'scheduled'];
  const published: WorkflowStatus[] = ['published', 'updated'];

  if (planning.includes(status)) return 'planning';
  if (production.includes(status)) return 'production';
  if (published.includes(status)) return 'published';
  return 'terminal';
}

// Get urgency label
export function getUrgencyLabel(urgency: number): string {
  if (urgency === 1) return 'Flash';
  if (urgency === 2) return 'Urgent';
  if (urgency <= 4) return 'High Priority';
  if (urgency === 5) return 'Normal';
  if (urgency <= 7) return 'Low Priority';
  return 'Evergreen';
}

// NOTE: estimateDuration and formatDuration are now defined in the
// TV DURATION section earlier in this file (for TV scripting workflow)

// Parse ISO 8601 duration to seconds
export function parseDuration(duration: string): number {
  // Simple parser for HH:MM:SS format
  const parts = duration.split(':').map(Number);
  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }
  return parts[0] || 0;
}


// =============================================================================
// BROADCAST FORMAT LABELS
// =============================================================================

export const BROADCAST_ITEM_LABELS: Record<BroadcastItemType, string> = {
  'VO': 'Voice Over',
  'SOT': 'Sound on Tape',
  'PKG': 'Package',
  'LIVE': 'Live Shot',
  'BREAK': 'Break',
  'TEASE': 'Tease',
  'BUMP': 'Bump',
};


// =============================================================================
// WORKFLOW STATUS LABELS & COLORS
// =============================================================================

export const WORKFLOW_STATUS_LABELS: Record<WorkflowStatus, string> = {
  'idea': 'Idea',
  'pitched': 'Pitched',
  'assigned': 'Assigned',
  'draft': 'Draft',
  'ai_review': 'AI Review',
  'submitted': 'Submitted',
  'editing': 'Editing',
  'in_review': 'In Review',
  'needs_revision': 'Needs Revision',
  'approved': 'Approved',
  'scheduled': 'Scheduled',
  'published': 'Published',
  'updated': 'Updated',
  'killed': 'Killed',
  'withdrawn': 'Withdrawn',
  'archived': 'Archived',
};

export const WORKFLOW_STATUS_COLORS: Record<WorkflowStatus, string> = {
  'idea': '#9CA3AF',
  'pitched': '#60A5FA',
  'assigned': '#34D399',
  'draft': '#FBBF24',
  'ai_review': '#8B5CF6',  // Purple for AI review (journalist's domain)
  'submitted': '#F97316',
  'editing': '#A78BFA',
  'in_review': '#EC4899',
  'needs_revision': '#EF4444',
  'approved': '#10B981',
  'scheduled': '#06B6D4',
  'published': '#22C55E',
  'updated': '#14B8A6',
  'killed': '#6B7280',
  'withdrawn': '#9CA3AF',
  'archived': '#D1D5DB',
};
